// HOOK: usePayroll - VERSIÓN CORREGIDA CON BACKEND REAL
// ═══════════════════════════════════════════════════════════════
import { useState, useCallback } from 'react';
import {
  createPayroll as apiCreatePayroll,
  getPayrolls as apiGetPayrolls,
  getPayrollDetails as apiGetPayrollDetails,
  validatePayrollDuplicate as apiValidatePayrollDuplicate,
  getEligibleEmployees as apiGetEligibleEmployees,
  processPayroll as apiProcessPayroll,
  closePayroll as apiClosePayroll,
  exportPayrollToExcel as apiExportPayrollToExcel,
  exportPayrollToPDF as apiExportPayrollToPDF,
  getPayrollSummary as apiGetPayrollSummary,
  getAccountingSummary as apiGetAccountingSummary,
  exportAccountingToExcel as apiExportAccountingToExcel,
  exportAccountingToPDF as apiExportAccountingToPDF,
  cancelPayroll as apiCancelPayroll,
  reopenPayroll as apiReopenPayroll,
  getPayrollAuditLog as apiGetPayrollAuditLog,
  generatePayslip as apiGeneratePayslip,
  generateBulkPayslips as apiGenerateBulkPayslips,
  comparePayrolls as apiComparePayrolls
} from '../api/payroll';
import { useSocialSecurityConfig } from './useSocialSecurityConfig';
import { useVariableIncomes } from './useVariableIncomes';
import { usePrestamos } from './usePrestamos';
import { calculateEmployeeISR } from '../components/Payroll/ISRModule/ISRModule';
import { aplicarDescuentoPrestamo } from '../components/Payroll/PrestamosModule';

export function usePayroll() {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const { calculateSS, getEffectiveConfig, globalConfig } = useSocialSecurityConfig();
  const { getVariableIncomes } = useVariableIncomes();
  const { prestamos, updatePrestamo } = usePrestamos();

  // ─────────────────────────────────────────────────────────────
  // 1️⃣ CREAR NÓMINA
  // ─────────────────────────────────────────────────────────────
  
  const createPayroll = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📋 Creando nómina:', data);
      
      if (!data.employeeIds || data.employeeIds.length === 0) {
        throw new Error('Debes seleccionar al menos un empleado');
      }
      
      // Verificar duplicado
      const isDuplicate = await apiValidatePayrollDuplicate(
        data.companyId,
        data.periodo,
        data.quincena,
        data.tipo
      );
      
      if (isDuplicate.exists && isDuplicate.payroll?.estado !== 'draft') {
        throw new Error('Ya existe una nómina para este período');
      }
      
      // Si existe en draft, reutilizarla
      if (isDuplicate.exists && isDuplicate.payroll?.estado === 'draft') {
        console.log('✅ Reutilizando nómina draft:', isDuplicate.payroll.id);
        return isDuplicate.payroll;
      }
      
      // Crear nómina nueva
      const result = await apiCreatePayroll(data);
      console.log('✅ Nómina creada:', result);
      
      return result.payroll;
      
    } catch (err) {
      console.error('❌ Error creando nómina:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 2️⃣ OBTENER EMPLEADOS ELEGIBLES
  // ─────────────────────────────────────────────────────────────
  
  const getEligibleEmployeesForPayroll = useCallback(async (companyId, periodo) => {
    try {
      setLoading(true);
      const result = await apiGetEligibleEmployees(companyId, periodo);
      return result.employees || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 3️⃣ PROCESAR NÓMINA (BACKEND)
  // ─────────────────────────────────────────────────────────────
  
  const processPayroll = useCallback(async (payrollId, employeeIds) => {
    try {
      setProcessing(true);
      console.log(`🚀 Procesando nómina ${payrollId} con ${employeeIds.length} empleados`);
      
      const result = await apiProcessPayroll(payrollId, employeeIds);
      
      console.log('✅ Nómina procesada:', result);
      return result;
      
    } catch (err) {
      console.error('❌ Error procesando nómina:', err);
      setError(err.message);
      throw err;
    } finally {
      setProcessing(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 4️⃣ CERRAR NÓMINA
  // ─────────────────────────────────────────────────────────────
  
  const closePayroll = useCallback(async (payrollId, userId) => {
    try {
      setLoading(true);
      const result = await apiClosePayroll(payrollId, userId);
      console.log('✅ Nómina cerrada');
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 5️⃣ OBTENER NÓMINAS
  // ─────────────────────────────────────────────────────────────
  
  const getPayrolls = useCallback(async (companyId, filters = {}) => {
    try {
      setLoading(true);
      const result = await apiGetPayrolls(companyId, filters);
      
      // Manejar diferentes formatos de respuesta
      if (Array.isArray(result)) {
        return result;
      } else if (result && Array.isArray(result.payrolls)) {
        return result.payrolls;
      } else if (result && result.data && Array.isArray(result.data)) {
        return result.data;
      }
      
      return [];
    } catch (err) {
      console.error('Error getting payrolls:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 6️⃣ EXPORTAR
  // ─────────────────────────────────────────────────────────────

  const exportToExcel = useCallback(async (payrollId) => {
    try {
      setLoading(true);
      const blob = await apiExportPayrollToExcel(payrollId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nomina_${payrollId}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Nómina exportada a Excel');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportToPDF = useCallback(async (payrollId) => {
    try {
      setLoading(true);
      const blob = await apiExportPayrollToPDF(payrollId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nomina_${payrollId}_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Nómina exportada a PDF');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportAccountingToExcel = useCallback(async (payrollId) => {
    try {
      setLoading(true);
      const blob = await apiExportAccountingToExcel(payrollId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resumen_contable_${payrollId}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Resumen contable exportado a Excel');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportAccountingToPDF = useCallback(async (payrollId) => {
    try {
      setLoading(true);
      const blob = await apiExportAccountingToPDF(payrollId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resumen_contable_${payrollId}_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Resumen contable exportado a PDF');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // 7️⃣ COMPROBANTES DE PAGO
  // ─────────────────────────────────────────────────────────────

  const downloadPayslip = useCallback(async (payrollId, employeeId, employeeCode) => {
    try {
      setLoading(true);
      const blob = await apiGeneratePayslip(payrollId, employeeId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante_${employeeCode}_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Comprobante descargado');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadBulkPayslips = useCallback(async (payrollId) => {
    try {
      setLoading(true);
      const blob = await apiGenerateBulkPayslips(payrollId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobantes_nomina_${payrollId}_${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Comprobantes masivos descargados');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Estados
    loading,
    processing,
    error,

    // Funciones principales
    createPayroll,
    getEligibleEmployees: getEligibleEmployeesForPayroll,
    processPayroll,
    closePayroll,
    getPayrolls,
    exportToExcel,
    exportToPDF,
    exportAccountingToExcel,
    exportAccountingToPDF,
    downloadPayslip,
    downloadBulkPayslips,

    // Funciones adicionales del API
    getPayrollDetails: apiGetPayrollDetails,
    getPayrollSummary: apiGetPayrollSummary,
    getAccountingSummary: apiGetAccountingSummary,
    comparePayrolls: apiComparePayrolls,
    cancelPayroll: apiCancelPayroll,
    reopenPayroll: apiReopenPayroll,
    getPayrollAuditLog: apiGetPayrollAuditLog,

    // Utilidades
    clearError: () => setError(null)
  };
}

export default usePayroll;
